import {
  Archive,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleHelp,
  CirclePause,
  CircleSlash,
  CircleUser,
  CircleX,
  Clock,
  LoaderCircle,
  type LucideIcon,
  Plug,
  TriangleAlert,
  Unplug,
  Wrench,
} from "lucide-react";

import {
  InvocationStatus,
  TriggerEvaluationStatus,
} from "@/shared/domain/actions";
import { AgentThreadStatus } from "@/shared/domain/agentThread";
import { CustomFieldStatus } from "@/shared/domain/customFields";
import { IngestionLabel } from "@/shared/domain/files";
import { type OrgLlmConnectionStatus } from "@/shared/domain/orgs";
import { TriggerDispatchStatus } from "@/shared/domain/triggers";
import { type StatusTone } from "@/shared/foundation/primitives";

declare const fromRegistry: unique symbol;

/**
 * How one status is drawn: its label, tone and icon. Only this file makes
 * them, so every status → colour decision lives here.
 */
export type StatusBadgeSpec = {
  readonly label: string;
  readonly tone: StatusTone;
  readonly icon: LucideIcon;
} & { readonly [fromRegistry]: true };

function spec(label: string, tone: StatusTone, icon: LucideIcon) {
  return { label, tone, icon } as StatusBadgeSpec;
}

const pending = spec("Pending", "neutral", Clock);
const running = spec("Running", "info", LoaderCircle);
const completed = spec("Completed", "success", CircleCheck);
const failed = spec("Failed", "danger", CircleX);

// Each map is keyed by its whole status type, so a status added to the domain
// is a compile error here until it has a badge.

const INVOCATION: Record<InvocationStatus, StatusBadgeSpec> = {
  [InvocationStatus.Queued]: pending,
  [InvocationStatus.Scheduled]: spec("Provisioning", "info", LoaderCircle),
  [InvocationStatus.Downloading]: running,
  [InvocationStatus.Processing]: running,
  [InvocationStatus.Uploading]: running,
  [InvocationStatus.Completed]: completed,
  [InvocationStatus.Cancelled]: spec("Cancelled", "neutral", CircleSlash),
  [InvocationStatus.Failed]: failed,
  [InvocationStatus.Deadly]: failed,
};

const SESSION = {
  in_progress: spec("In progress", "info", CircleDot),
  complete: spec("Complete", "success", CircleCheck),
} satisfies Record<"in_progress" | "complete", StatusBadgeSpec>;

/** A session's ingestion, beside its status. Ingesting is amber, not the
 * file label's blue, so it differs from the session's In progress. */
const SESSION_INGESTION = {
  ingesting: spec("Ingesting…", "warning", LoaderCircle),
  failed: spec("Ingestion failed", "danger", CircleX),
  ingested: spec("Ingested", "success", CircleCheck),
  checking: spec("Checking…", "neutral", CircleDashed),
  unknown: spec("Status unknown", "neutral", CircleHelp),
} satisfies Record<string, StatusBadgeSpec>;

const FILE_INGESTION: Record<IngestionLabel, StatusBadgeSpec> = {
  [IngestionLabel.Ingested]: spec(
    IngestionLabel.Ingested,
    "success",
    CircleCheck,
  ),
  [IngestionLabel.Ingesting]: spec(
    IngestionLabel.Ingesting,
    "info",
    LoaderCircle,
  ),
  [IngestionLabel.PartlyIngested]: spec(
    IngestionLabel.PartlyIngested,
    "warning",
    TriangleAlert,
  ),
  [IngestionLabel.IngestionFailed]: spec(
    IngestionLabel.IngestionFailed,
    "danger",
    CircleX,
  ),
  [IngestionLabel.AwaitingIngestion]: spec(
    IngestionLabel.AwaitingIngestion,
    "neutral",
    Clock,
  ),
  [IngestionLabel.NotWaitedFor]: spec(
    IngestionLabel.NotWaitedFor,
    "neutral",
    CircleSlash,
  ),
  [IngestionLabel.StoredAsIs]: spec(
    IngestionLabel.StoredAsIs,
    "neutral",
    Archive,
  ),
};

const AGENT_THREAD: Record<AgentThreadStatus, StatusBadgeSpec> = {
  [AgentThreadStatus.NOT_STARTED]: spec("Not started", "neutral", CircleDashed),
  [AgentThreadStatus.USER_TURN]: spec("User turn", "warning", CircleUser),
  [AgentThreadStatus.ROBOTO_TURN]: spec("Working", "info", LoaderCircle),
  [AgentThreadStatus.CLIENT_TOOL_TURN]: spec("Client tools", "info", Wrench),
  [AgentThreadStatus.GOALS_FAILED]: spec("Goals failed", "danger", CircleX),
};

const MCP = {
  connected: spec("Connected", "success", Plug),
  expired: spec("Expired", "warning", CircleAlert),
  not_connected: spec("Not connected", "neutral", Unplug),
  set: spec("Set", "success", CircleCheck),
  not_set: spec("Not set", "neutral", CircleDashed),
  unknown: spec("Unknown", "neutral", CircleHelp),
} satisfies Record<string, StatusBadgeSpec>;

const MODEL_VERIFICATION = {
  verified: spec("Verified", "success", CircleCheck),
  not_verified: spec("Not verified", "warning", CircleAlert),
  failed: spec("Failed", "danger", CircleX),
  unavailable: spec("Unavailable", "danger", CircleX),
} satisfies Record<string, StatusBadgeSpec>;

const LLM_CONNECTION: Record<OrgLlmConnectionStatus, StatusBadgeSpec> = {
  active: spec("Active", "success", CircleCheck),
  failing: spec("Failing", "danger", CircleX),
  disabled: spec("Disabled", "neutral", CirclePause),
};

/** When an LLM connection last proved it works. */
const LLM_CONNECTION_VERIFICATION = {
  verified: spec("Verified", "success", CircleCheck),
  not_verified: spec("Not verified yet", "neutral", CircleDashed),
} satisfies Record<string, StatusBadgeSpec>;

const CUSTOM_FIELD: Record<CustomFieldStatus, StatusBadgeSpec> = {
  [CustomFieldStatus.Ready]: spec("Ready", "success", CircleCheck),
  [CustomFieldStatus.Creating]: spec("Creating", "info", LoaderCircle),
  [CustomFieldStatus.Deleting]: spec("Deleting", "info", LoaderCircle),
  [CustomFieldStatus.Failed]: failed,
};

const TRIGGER_DISPATCH: Record<TriggerDispatchStatus, StatusBadgeSpec> = {
  [TriggerDispatchStatus.Claimed]: spec("In progress", "info", LoaderCircle),
  [TriggerDispatchStatus.Dispatched]: spec(
    "Dispatched",
    "success",
    CircleCheck,
  ),
  [TriggerDispatchStatus.Failed]: failed,
  [TriggerDispatchStatus.Shadow]: spec("Shadow", "neutral", CircleDashed),
  [TriggerDispatchStatus.Unknown]: spec("Unknown", "warning", CircleHelp),
};

const TRIGGER_EVALUATION: Record<TriggerEvaluationStatus, StatusBadgeSpec> = {
  [TriggerEvaluationStatus.Pending]: pending,
  [TriggerEvaluationStatus.Evaluated]: spec(
    "Evaluated",
    "success",
    CircleCheck,
  ),
  [TriggerEvaluationStatus.Failed]: failed,
};

/** An S3 bucket's health check. Unrecognised statuses read as unverified. */
const S3_HEALTH = {
  healthy: spec("Healthy", "success", CircleCheck),
  unhealthy: spec("Unhealthy", "danger", CircleX),
  unknown: spec("Unknown", "warning", CircleHelp),
  unverified: spec("Unverified", "neutral", CircleDashed),
} satisfies Record<string, StatusBadgeSpec>;

const INGESTION_RULE = {
  paused: spec("Paused", "warning", CirclePause),
} satisfies Record<string, StatusBadgeSpec>;

/** Whether admins have dealt with a piece of agent feedback. An admin's own
 * label replaces the word. */
const AGENT_FEEDBACK = {
  open: spec("Open", "warning", CircleDot),
  resolved: spec("Resolved", "success", CircleCheck),
} satisfies Record<string, StatusBadgeSpec>;

/** Which of an MCP server's tools agents may call. "N/M enabled" replaces
 * the label once they are configured. */
const MCP_ALLOWED_TOOLS = {
  not_configured: spec("Not configured", "neutral", CircleDashed),
  none_enabled: spec("None enabled", "warning", CircleAlert),
  enabled: spec("Enabled", "success", CircleCheck),
} satisfies Record<string, StatusBadgeSpec>;

/** The skill version the AI invokes, against the latest. The version, "v3",
 * replaces the label. */
const SKILL_VERSION = {
  latest: spec("Latest", "neutral", CircleCheck),
  stale: spec("Outdated", "warning", CircleAlert),
} satisfies Record<string, StatusBadgeSpec>;

/** Every status the app draws, by domain. */
export const STATUS_BADGES = {
  invocation: INVOCATION,
  session: SESSION,
  sessionIngestion: SESSION_INGESTION,
  fileIngestion: FILE_INGESTION,
  agentThread: AGENT_THREAD,
  mcp: MCP,
  modelVerification: MODEL_VERIFICATION,
  llmConnection: LLM_CONNECTION,
  llmConnectionVerification: LLM_CONNECTION_VERIFICATION,
  customField: CUSTOM_FIELD,
  triggerDispatch: TRIGGER_DISPATCH,
  triggerEvaluation: TRIGGER_EVALUATION,
  s3Health: S3_HEALTH,
  ingestionRule: INGESTION_RULE,
  agentFeedback: AGENT_FEEDBACK,
  mcpAllowedTools: MCP_ALLOWED_TOOLS,
  skillVersion: SKILL_VERSION,
} as const;

/** A bucket's health status, which the API sends as a free string. */
export function s3HealthBadge(status: string): StatusBadgeSpec {
  return status in S3_HEALTH
    ? S3_HEALTH[status as keyof typeof S3_HEALTH]
    : S3_HEALTH.unverified;
}
